/**
 * Типизированный REST-клиент kikayoo (Фаза 1; полиморфные типы — Фаза 2/И5).
 *
 * В dev Vite проксирует `/api` на Go-сервер `http://127.0.0.1:3000`
 * (см. vite.config.ts); в проде запросы идут в тот же origin. Поэтому все пути
 * здесь — относительные (`/api/...`). Контракты 1:1 с `server/internal/api`.
 */
import { genId } from './drafts';
import type { DraftQuestion, DraftQuestionBody, QuizDraft } from './drafts';

/** Метаданные игры (без payload) — поле ответа GET/POST /api/games. */
export interface GameMeta {
  id: number;
  type: string;
  /** Назначение внутри library (И4): 'showcase' | 'questions'; отсутствует для личных квизов. */
  category?: string;
  title: string;
  /** ISO-timestamp создания. */
  createdAt: string;
}

/** Полное описание игры: метаданные + JSON-строка квиза (payload). */
export interface GameFull extends GameMeta {
  /** JSON-строка по формату QuizPayload: `{"questions":[...]}`. */
  payload: string;
}

/** Один вопрос квиза (для построения payload при создании игры). */
export interface QuizQuestion {
  title: string;
  options: string[];
  /** 0-based индекс правильного варианта. */
  correctIndex: number;
  /** Длительность в мс; 0/отсутствие = серверный дефолт (20000). */
  durationMs?: number;
}

/** Формат `games.payload` (game.QuizPayload). */
export interface QuizPayload {
  questions: QuizQuestion[];
}

/** Ответ POST /api/sessions: PIN + id сессии для подключения хоста по WS. */
export interface StartSessionResponse {
  pin: string;
  sessionId: number;
}

/** Строка итогового лидерборда (GET /api/sessions/{id}/results). */
export interface PlayerResult {
  playerId: number;
  nickname: string;
  score: number;
  answered: number;
  correct: number;
  avgMs: number;
}

/** Агрегированная статистика по вопросу сессии. */
export interface QuestionStat {
  questionId: number;
  total: number;
  correct: number;
}

/** Ответ GET /api/sessions/{id}/results. */
export interface SessionResults {
  sessionId: number;
  playerCount: number;
  answerCount: number;
  correctCount: number;
  players: PlayerResult[];
  questions: QuestionStat[];
}

/** Ошибка REST-вызова: HTTP-статус + распарсенное сообщение + (опц.) тело ответа. */
export class ApiError extends Error {
  readonly status: number;
  /**
   * Распарсенное тело ответа при ошибке (объект JSON), либо undefined. Нужен для
   * структурированных ошибок, где кроме кода есть payload: напр., 409 при удалении
   * referenced-картинки отдаёт {error:"referenced", games:[id,...]} — UI читает
   * games отсюда, чтобы показать список ссылающихся квизов (И5). Для простых ошибок
   * (401/403/404/500) поле может отсутствовать.
   */
  readonly body: Record<string, unknown> | undefined;
  constructor(status: number, message: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Сериализует квиз в JSON-строку payload (формат game.QuizPayload). */
export function encodeQuizPayload(quiz: QuizPayload): string {
  return JSON.stringify(quiz);
}

/**
 * Возвращает «голый» путь upload-URL, удаляя query (?..) и fragment (#..).
 *
 * Фаза 6, И2: сервер подписывает /uploads/<file> в transport-слое (secure_link),
 * поэтому URL, пришедший с бэкенда (GET /api/games, POST /api/uploads), может
 * содержать ?md5=..&expires=... Но в games.payload (БД) обязан храниться
 * «голый» путь — иначе: (а) валидация сервера отклонит query (isValidUploadPath),
 * (б) подпись протухнет и сохранённый квиз «сломается». Эта функция вызывается при
 * СОХРАНЕНИИ квиза (PUT/POST через encodeDraftQuiz), гарантируя чистый путь в БД;
 * подпись навешивается заново при каждой отдаче клиенту. Для не-/uploads/ URL и URL
 * без query/fragment — no-op (возвращается как есть).
 */
export function bareUploadURL(url: string): string {
  if (typeof url !== 'string' || url.length === 0) {
    return url;
  }
  let cut = url.length;
  const q = url.indexOf('?');
  if (q >= 0) cut = Math.min(cut, q);
  const h = url.indexOf('#');
  if (h >= 0) cut = Math.min(cut, h);
  return cut === url.length ? url : url.slice(0, cut);
}

/** Тело POST /api/games. */
export interface CreateGameBody {
  title: string;
  /** Квиз (будет сериализован в JSON-строку). */
  quiz: QuizPayload;
  type?: string;
}

/**
 * Низкоуровневый запрос: бросает ApiError на !ok, иначе парсит JSON (или {}
 * для пустого тела). `fetchImpl` инъектируется для тестов.
 *
 * `credentials: 'include'` выставляется всегда: сессия живёт в httpOnly cookie
 * `kikayoo_session` (Фаза 3, И2/И3), который JS прочитать не может — поэтому
 * браузер должен отправлять его с каждым запросом. Для анонимных эндпоинтов
 * (создание игр и т.п.) cookie игнорируется сервером, так что это безопасно и
 * не ломает обратную совместимость.
 */
export async function request<T>(
  path: string,
  init: RequestInit,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const res = await fetchImpl(path, { ...init, credentials: 'include' });
  const text = await res.text();
  let body: unknown = {};
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      // Нечитаемое тело — оставим пустым; статус уже учтён ниже.
      body = { error: text };
    }
  }
  if (!res.ok) {
    const message = isRecord(body) && typeof body.error === 'string' ? body.error : res.statusText;
    // Тело передаётся в ApiError, чтобы caller мог прочитать структурированные поля
    // ошибки (напр., games[] из 409 referenced при удалении картинки — И5).
    throw new ApiError(res.status, message, isRecord(body) ? body : undefined);
  }
  return body as T;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export const JSON_HEADERS: Record<string, string> = { 'Content-Type': 'application/json' };

// ─── Слайд-капча auth-форм ведущих (И8) ───────────────────────────────────────
//
// GET /api/captcha/slide — публичный эндпоинт (без auth): капча нужна до входа.
// Возвращает готовые data-URI для <img src>. При CAPTCHA_ENABLED=0 сервер отвечает
// 503 — тогда getCaptcha бросает ApiError(503), и виджет капчи скрывается (форма
// сабмитится без капчи, серверные проверки — no-op). Стратегия: register/forgot —
// капча ВСЕГДА; login — адаптивно (после K неудач сервер вернёт код ниже).

/** Выданная слайд-капча: фон с «дыркой» + кусок-пазл + вертикальная позиция куска. */
export interface SlideCaptcha {
  /** Непрозрачный id — клиент возвращает его в register/login/forgot как captchaId. */
  id: string;
  /** Фон с «дыркой» (JPEG, data-URI) — для <img src>. */
  bg: string;
  /** Кусок-пазл (PNG, data-URI) — пользователь двигает его поверх фона. */
  tile: string;
  /** Вертикальная позиция куска (px от верха фона, в_NATIVE координатах 300×220). */
  tileY: number;
}

/** Решение слайд-капчи — то, что клиент шлёт обратно в register/login/forgot. */
export interface CaptchaSolution {
  captchaId: string;
  /** Позиция ползунка при отпускании (px от левого края, native-координаты фона). */
  captchaX: number;
}

/**
 * GET /api/captcha/slide → 200 SlideCaptcha. 503 — капча отключена (виджет
 * скрывается, форма сабмитится без капчи). 429 — IP rate-limit. `fetchImpl`
 * инъектируется для тестов.
 */
export async function getCaptcha(fetchImpl: typeof fetch = fetch): Promise<SlideCaptcha> {
  return request<SlideCaptcha>('/api/captcha/slide', { method: 'GET' }, fetchImpl);
}

/** Результат пред-сабмит проверки капчи: ok=true — решение верно (капча валидна). */
export interface CaptchaVerifyResult {
  ok: boolean;
}

/**
 * POST /api/captcha/verify → 200 {ok:boolean} — пред-сабмит серверная проверка
 * решения (П5, Фаза 9). ok=true: решение верно (капча ОСТАЁТСЯ валидной для
 * последующей проверки в register — не погашена). ok=false: неверно/просрочена/
 * повторна (при этом неверная погашается — нужно перевыпустить новую).
 * 503 — капча отключена. 429 — IP rate-limit. `fetchImpl` инъектируется для тестов.
 *
 * Безопасность идентична register-time проверке: ошибочная попытка расходует капчу
 * (1 попытка на id — анти-брутфорс), перевыпуск rate-limited.
 */
export async function verifyCaptcha(
  solution: CaptchaSolution,
  fetchImpl: typeof fetch = fetch,
): Promise<CaptchaVerifyResult> {
  return request<CaptchaVerifyResult>(
    '/api/captcha/verify',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ captchaId: solution.captchaId, captchaX: solution.captchaX }),
    },
    fetchImpl,
  );
}

// Коды ошибок капчи приходят в теле 400-ответа как {"error": "<код>"} (см. поле
// ApiError.message). Экраны различают их для UX: показать капчу vs. обновить.
/** Сервер требует капчу, но она не прислана (login после K неудач). */
export const CAPTCHA_REQUIRED = 'captcha_required';
/** Капча прислана, но не пройдена (неверно/просрочена/повторно). */
export const CAPTCHA_FAILED = 'captcha_failed';

/** POST /api/games → 201 GameMeta. */
export async function createGame(
  body: CreateGameBody,
  fetchImpl: typeof fetch = fetch,
): Promise<GameMeta> {
  return request<GameMeta>(
    '/api/games',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        title: body.title,
        type: body.type ?? 'quiz',
        payload: encodeQuizPayload(body.quiz),
      }),
    },
    fetchImpl,
  );
}

/** Опции запроса списка игр GET /api/games (И4: публичный листинг — только library). */
export interface ListGamesOptions {
  /** Фильтр по типу (например 'library'); публично отдаётся только общий контент. */
  type?: string;
  /** Фильтр по category внутри library: 'showcase' (Открытая витрина) | 'questions'
   * (Библиотека вопросов). Применяется вместе с type='library'. */
  category?: string;
  /** Язык шаблонов «Библиотеки вопросов» (library-i18n, v14): 'ru' | 'en' | 'cn'.
   * Сервер применяет фильтр ТОЛЬКО при category='questions' (whitelist; невалидный
   * или отсутствующий → 'ru'); showcase и листинг без category не затрагиваются. */
  lang?: string;
  fetchImpl?: typeof fetch;
}

/**
 * GET /api/games[?type=&category=&lang=] → 200 GameMeta[] (от новых к старым). Публично
 * отдаётся только общий контент (type='library'); личные квизы (type='quiz') НЕ
 * листаются (приватность И4) — свои через listMyGames(). При category фильтр сужается
 * до секции (showcase/questions); lang — язык секции questions (v14).
 */
export async function listGames(options: ListGamesOptions = {}): Promise<GameMeta[]> {
  const { type, category, lang, fetchImpl = fetch } = options;
  const params = new URLSearchParams();
  if (type && type.length > 0) params.set('type', type);
  if (category && category.length > 0) params.set('category', category);
  if (lang && lang.length > 0) params.set('lang', lang);
  const query = params.toString();
  const path = query.length > 0 ? `/api/games?${query}` : '/api/games';
  const games = await request<GameMeta[] | null>(path, { method: 'GET' }, fetchImpl);
  return games ?? [];
}

/** Опции listLibraryGames (opts-объект вместо позиционного fetchImpl). */
export interface ListLibraryGamesOptions {
  /** Язык секции questions (library-i18n: сервер фильтрует только category='questions'). */
  lang?: string;
  fetchImpl?: typeof fetch;
}

/**
 * GET /api/games?type=library[&category=&lang=] → 200 GameMeta[] (общий контент:
 * Открытая витрина / Библиотека вопросов, И4). category='showcase' — тематические
 * квизы (Космос/Звёздные войны/Наука); 'questions' — шаблоны по типам вопросов на
 * языке lang (v14: ru/en/cn, дефолт ru); без category — весь library.
 */
export function listLibraryGames(
  category?: string,
  opts: ListLibraryGamesOptions = {},
): Promise<GameMeta[]> {
  return listGames({ type: 'library', category, lang: opts.lang, fetchImpl: opts.fetchImpl });
}

/** GET /api/games/{id} → 200 GameFull; 404 если игры нет. */
export async function getGame(id: number, fetchImpl: typeof fetch = fetch): Promise<GameFull> {
  return request<GameFull>(`/api/games/${id}`, { method: 'GET' }, fetchImpl);
}

// ─── Личный кабинет (И4): мои квизы, удаление и правка ─────────────────────────
//
// Эндпоинты под `RequireAuth` (401 без сессии); POST/PUT дополнительно требуют
// verified-аккаунта (403). GET /api/me/games отдаёт только квизы текущего
// владельца (library-seed и чужие игры исключены), поэтому экран кабинета может
// показывать действия «Провести/Редактировать/Удалить» без доп. фильтрации.

/** Метаданные моего квиза (GET /api/me/games). Структурно совпадает с GameMeta. */
export type MyGame = GameMeta;

/** GET /api/me/games → 200 MyGame[] (только мои квизы, от новых к старым); 401 без auth. */
export async function fetchMyGames(fetchImpl: typeof fetch = fetch): Promise<MyGame[]> {
  const games = await request<MyGame[] | null>('/api/me/games', { method: 'GET' }, fetchImpl);
  return games ?? [];
}

/** Тело PUT /api/games/{id}: частичное обновление (title и/или payload). */
export interface UpdateGameBody {
  title?: string;
  /** JSON-строка wire-формата квиза (game.QuizPayload); опц. */
  payload?: string;
}

/**
 * PUT /api/games/{id} (RequireAuth+RequireVerified) `{title?, payload?}` →
 * 200 GameFull (актуальные метаданные + payload). 403 (чужой/library/unverified),
 * 404 (нет игры), 401 (без auth).
 */
export async function updateGame(
  id: number,
  body: UpdateGameBody,
  fetchImpl: typeof fetch = fetch,
): Promise<GameFull> {
  return request<GameFull>(
    `/api/games/${id}`,
    { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body) },
    fetchImpl,
  );
}

/**
 * DELETE /api/games/{id} (RequireAuth) → 204 (свой). 403 (чужой/library), 404 (нет),
 * 401 (без auth). Возвращает void; на ошибку бросает ApiError со статусом.
 */
export async function deleteGame(id: number, fetchImpl: typeof fetch = fetch): Promise<void> {
  await request<unknown>(`/api/games/${id}`, { method: 'DELETE' }, fetchImpl);
}

/** POST /api/sessions { gameId } → 201 { pin, sessionId }; 404 если игры нет. */
export async function startSession(
  gameId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<StartSessionResponse> {
  return request<StartSessionResponse>(
    '/api/sessions',
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ gameId }) },
    fetchImpl,
  );
}

// Код ошибки превышения лимита параллельных комнат хоста (limits.parallel_rooms):
// POST /api/sessions отвечает 409 {"error":"limit_parallel_rooms"} (см. handlers.go,
// hub.ErrParallelRooms). Фронт различает этот случай, чтобы показать inline-сообщение
// в кабинете БЕЗ редиректа на витрину квизов (Dashboard.handleLaunch).
export const LIMIT_PARALLEL_ROOMS = 'limit_parallel_rooms';

/** GET /api/sessions/{id}/results → 200 SessionResults; 404 если сессии нет. */
export async function getSessionResults(
  sessionId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<SessionResults> {
  return request<SessionResults>(
    `/api/sessions/${sessionId}/results`,
    { method: 'GET' },
    fetchImpl,
  );
}

// ─── Личный кабинет: активные сессии ведущего (host-scoped /api/me/sessions) ─
//
// GET /api/me/sessions/active и POST /api/me/sessions/{id}/stop — host-scoped
// подмножество admin-эндпоинтов: сервер фильтрует Snapshot() по HostUserID из
// контекста, проверка владения перед stop (чужая сессия → 404 без раскрытия).
// Структура идентична серверному hub.RoomInfo, но hostUserId не отдаётся
// (хост и так знает свой аккаунт) — поэтому заведён отдельный тип MyActiveSession.

/** Моя активная сессия (host-scoped RoomInfo из GET /api/me/sessions/active). */
export interface MyActiveSession {
  pin: string;
  gameId: number;
  sessionId: number;
  /** Кол-во подключённых (WS-клиентов комнаты, включая самого хоста — как в admin). */
  playerCount: number;
  /** Текущий вопрос; опц. (0 в lobby/finished → опускается сервером через omitempty). */
  currentQuestionId?: number;
  /** Фаза движка: lobby/question_active/question_closed/finished (game.Phase). */
  phase: string;
  /** ISO8601 UTC — момент создания комнаты в реестре. */
  startedAt: string;
}

/**
 * GET /api/me/sessions/active → 200 MyActiveSession[] (только свои живые комнаты;
 * возможно пустой, никогда null). 401 — без auth; 403 — аккаунт заблокирован.
 */
export async function fetchMyActiveSessions(
  fetchImpl: typeof fetch = fetch,
): Promise<MyActiveSession[]> {
  const sessions = await request<MyActiveSession[] | null>(
    '/api/me/sessions/active',
    { method: 'GET' },
    fetchImpl,
  );
  return sessions ?? [];
}

/** Ответ POST /api/me/sessions/{id}/stop: {stopped: id} (200). */
export interface StopMySessionResponse {
  stopped: number;
}

/**
 * POST /api/me/sessions/{id}/stop → 200 {stopped: id} (остановка своей сессии:
 * закрыть комнату, разослать WS finished, пометить finished_at; БЕЗ audit).
 * 404 — сессия не активна или чужая (единый ответ без раскрытия существования);
 * 401 — без auth; 500 — внутр. ошибка.
 */
export function stopMySession(
  sessionId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<StopMySessionResponse> {
  return request<StopMySessionResponse>(
    `/api/me/sessions/${sessionId}/stop`,
    { method: 'POST' },
    fetchImpl,
  );
}

// ─── Личный кабинет: история live-сессий хоста (Фаза 10, Секция B, И6) ────────
//
// GET /api/me/sessions/history, GET /api/me/sessions/{id}/results и
// GET /api/me/stats — me-scoped история завершённых сессий. kind:'live' —
// дискриминатор-резерв стыка с открытыми квизами Секции C (§B5.2): фронт
// рендерит список по полю kind, не по умолчанию.

/** Строка истории: одна завершённая live-сессия хоста (элемент items). */
export interface MySessionHistoryItem {
  id: number;
  gameId: number;
  /** Название квиза (JOIN games.title на сервере). */
  gameTitle: string;
  /** Всегда 'live' в v1 — резерв под смешанную историю открытых квизов. */
  kind: 'live';
  /** ISO8601 UTC. */
  startedAt: string;
  /** ISO8601 UTC. */
  finishedAt: string;
  /** finishedAt − startedAt в секундах. */
  durationSec: number;
  playerCount: number;
}

/** Ответ GET /api/me/sessions/history: страница + total без LIMIT. */
export interface MySessionHistoryResp {
  items: MySessionHistoryItem[];
  total: number;
}

/**
 * GET /api/me/sessions/history?limit=&offset= → 200 {items, total} (завершённые
 * сессии текущего хоста от новых к старым; пустая история — {items:[], total:0}).
 * limit/offset опциональны (серверные дефолты 20/0). 401 — без auth.
 */
export async function fetchMySessionHistory(
  opts?: { limit?: number; offset?: number },
  fetchImpl: typeof fetch = fetch,
): Promise<MySessionHistoryResp> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return request<MySessionHistoryResp>(
    `/api/me/sessions/history${qs ? `?${qs}` : ''}`,
    { method: 'GET' },
    fetchImpl,
  );
}

/** Ответ GET /api/me/sessions/{id}/results: SessionResults + метаданные шапки. */
export interface MySessionResults extends SessionResults {
  gameId: number;
  gameTitle: string;
  kind: 'live';
  startedAt: string;
  finishedAt: string;
  durationSec: number;
}

/**
 * GET /api/me/sessions/{id}/results → 200 итоги своей сессии постфактум
 * (leaderboard + статистика вопросов + gameTitle/даты/длительность). 404 —
 * сессии нет или чужая (единый ответ без раскрытия существования).
 */
export async function fetchMySessionResults(
  sessionId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<MySessionResults> {
  return request<MySessionResults>(
    `/api/me/sessions/${sessionId}/results`,
    { method: 'GET' },
    fetchImpl,
  );
}

/**
 * Сводная статистика хоста по завершённым live-сессиям. first/lastFinishedAt
 * отсутствуют, если завершённых сессий ещё нет (серверный omitempty).
 */
export interface MyStats {
  gamesCount: number;
  sessionsFinished: number;
  totalPlayers: number;
  totalScore: number;
  firstFinishedAt?: string;
  lastFinishedAt?: string;
}

/** GET /api/me/stats → 200 MyStats (агрегаты только по завершённым). */
export async function fetchMyStats(fetchImpl: typeof fetch = fetch): Promise<MyStats> {
  return request<MyStats>('/api/me/stats', { method: 'GET' }, fetchImpl);
}

// ─── Полиморфный wire-формат квиза (game.QuizPayload на сервере, И5) ───────────
//
// Сервер парсит games.payload через game.ParseQuizPayload: JSON вида
// { "questions": [ {kind, title, durationMs?, <секция по kind>} ] }. Каждая
// type-секция хранит ПРАВИЛЬНЫЙ ответ (choice.correctIndex / open.accept /
// match.pairs / order.correctOrder); poll — без правильного. При отсутствии
// "kind" сервер трактует как choice (обратная совместимость со Фазой 1), но
// редактор всегда посылает явный kind. Эти типы отражают server game.Question.

export interface WireChoiceSection {
  options: string[];
  correctIndex: number;
}
export interface WirePollSection {
  options: string[];
}
export interface WireOpenSection {
  accept: string[];
  maxLen?: number;
}
export interface WireMatchPair {
  left: string;
  right: string;
}
export interface WireMatchSection {
  pairs: WireMatchPair[];
}
export interface WireOrderSection {
  items: string[];
  correctOrder: number[];
}
/** Wire-секция word-вопроса (облако слов, И8): maxLen, без правильного ответа. */
export interface WireWordSection {
  maxLen?: number;
}
/** Wire-секция multi-вопроса (Фаза 14, эпик C; server game.MultiDef). */
export interface WireMultiSection {
  options: string[];
  correct: number[];
}
/** Wire-секция run-вопроса «Беги к углу» (Фаза 14, эпик B4; server game.RunDef). */
export interface WireRunSection {
  corners: string[];
  correctIndex: number;
}

interface WireQuestionBase {
  title: string;
  /** 0/отсутствие → серверный дефолт (DefaultQuestionMs). */
  durationMs?: number;
  /** URL изображения вопроса (POST /api/uploads); опц., omitempty на сервере (И2/И3). */
  image?: string;
  /**
   * Многострочный блок кода вопроса (Фаза 16, B1/B4); опц., omitempty на
   * сервере, ровно один на вопрос. Лимит длины — limits.code_max_chars (руны).
   */
  code?: string;
  /** Язык блока кода из whitelist (game.CodeLangs); пусто при code => plaintext. */
  codeLang?: string;
}

/** Wire-вопрос: дискриминированное объединение по kind с type-секцией. */
export type WireQuestion =
  | (WireQuestionBase & { kind: 'choice'; choice: WireChoiceSection })
  | (WireQuestionBase & { kind: 'poll'; poll: WirePollSection })
  | (WireQuestionBase & { kind: 'open'; open: WireOpenSection })
  | (WireQuestionBase & { kind: 'match'; match: WireMatchSection })
  | (WireQuestionBase & { kind: 'order'; order: WireOrderSection })
  | (WireQuestionBase & { kind: 'word'; word: WireWordSection })
  | (WireQuestionBase & { kind: 'multi'; multi: WireMultiSection })
  | (WireQuestionBase & { kind: 'run'; run: WireRunSection });

/** Полный полиморфный payload квиза (формат games.payload). */
export interface WireQuizPayload {
  questions: WireQuestion[];
}

/**
 * Конвертирует редакторский черновик в полиморфный wire-payload (game.QuizPayload)
 * для POST /api/games. Правильный ответ переносится в type-секцию; kind задаётся
 * явно для каждого вопроса. Сервер валидирует консистентность через ParseQuizPayload.
 */
export function encodeDraftQuiz(draft: QuizDraft): WireQuizPayload {
  return { questions: draft.questions.map(draftQuestionToWire) };
}

function draftQuestionToWire(q: DraftQuestion): WireQuestion {
  const base: WireQuestionBase = { title: q.title };
  if (q.durationMs !== undefined && q.durationMs > 0) {
    base.durationMs = q.durationMs;
  }
  // image переносим только при непустом URL — пустая строка = «убрать» (omitempty на сервере).
  // Фаза 6, И2: в БД должен попасть «голый» путь — подпись secure_link (query)
  // срезается через bareUploadURL (подпись навешивается сервером при отдаче, не хранится).
  if (typeof q.image === 'string' && q.image.length > 0) {
    base.image = bareUploadURL(q.image);
  }
  // Блок кода (Фаза 16, B4): переносим только при непустом тексте — пустая
  // строка = «убрать блок» (omitempty на сервере). codeLang пишем ТОЛЬКО
  // вместе с code: пустой code + codeLang — структурная ошибка сервера (422
  // lang_without_code), поэтому одиночный codeLang отбрасываем.
  if (typeof q.code === 'string' && q.code.length > 0) {
    base.code = q.code;
    if (typeof q.codeLang === 'string' && q.codeLang.length > 0) {
      base.codeLang = q.codeLang;
    }
  }
  switch (q.body.kind) {
    case 'choice':
      return {
        ...base,
        kind: 'choice',
        choice: { options: [...q.body.options], correctIndex: q.body.correctIndex },
      };
    case 'poll':
      return { ...base, kind: 'poll', poll: { options: [...q.body.options] } };
    case 'open': {
      const open: WireOpenSection = { accept: [...q.body.accept] };
      if (q.body.maxLen !== undefined) {
        open.maxLen = q.body.maxLen;
      }
      return { ...base, kind: 'open', open };
    }
    case 'match':
      return {
        ...base,
        kind: 'match',
        match: { pairs: q.body.pairs.map((p) => ({ left: p.left, right: p.right })) },
      };
    case 'order':
      return {
        ...base,
        kind: 'order',
        order: { items: [...q.body.items], correctOrder: [...q.body.correctOrder] },
      };
    case 'word': {
      const word: WireWordSection = {};
      if (q.body.maxLen !== undefined) {
        word.maxLen = q.body.maxLen;
      }
      return { ...base, kind: 'word', word };
    }
    case 'multi':
      return {
        ...base,
        kind: 'multi',
        multi: { options: [...q.body.options], correct: [...q.body.correct].sort((a, b) => a - b) },
      };
    case 'run':
      // Плоское draft-тело → вложенная wire-секция run:{corners, correctIndex}
      // (§3.1; seed/params сервер добавляет сам при StartQuestion).
      return {
        ...base,
        kind: 'run',
        run: { corners: [...q.body.corners], correctIndex: q.body.correctIndex },
      };
  }
}

/**
 * Публикует черновик как новую игру (POST /api/games) и возвращает метаданные.
 * Полиморфный payload собирается через encodeDraftQuiz; заголовок — из черновика
 * (fallback «Квиз без названия»). При превышении rate-limit бросает ApiError со
 * status=429 — HostEditor показывает дружелюбное сообщение.
 */
export async function createGameFromDraft(
  draft: QuizDraft,
  fetchImpl: typeof fetch = fetch,
): Promise<GameMeta> {
  return request<GameMeta>(
    '/api/games',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        title: draft.title.trim() || 'Квиз без названия',
        type: 'quiz',
        payload: JSON.stringify(encodeDraftQuiz(draft)),
      }),
    },
    fetchImpl,
  );
}

// ─── Библиотека → черновик (И7) ────────────────────────────────────────────────
//
// Библиотечный квиз хранится в games.payload в формате game.QuizPayload (он же
// WireQuizPayload здесь). Мост «библиотечный квиз → черновик редактора» конвертирует
// wire-формат (type-секции choice/poll/open/match/order с правильным ответом) в
// редакторскую модель QuizDraft (вопросы с body), которую HostEditor умеет править
// и сохранять в localStorage. Правильный ответ переносится 1:1 (choice.correctIndex,
// open.accept, match.pairs, order.correctOrder); poll — без правильного, как обычно.

/**
 * Конвертирует библиотечный квиз (wire-формат) в редакторский черновик с НОВЫМ id.
 * Чистая функция: не трогает localStorage. Caller (обычно LibraryCatalog) вызывает
 * saveDraft(...) для сохранения и перехода в редактор. Бросает SyntaxError при
 * невалидном JSON payload (парсится через JSON.parse).
 */
export function wireQuizToDraft(wire: WireQuizPayload, title: string): QuizDraft {
  return {
    id: genId(),
    title,
    questions: wire.questions.map(wireQuestionToDraft),
    updatedAt: Date.now(),
  };
}

function wireQuestionToDraft(q: WireQuestion): DraftQuestion {
  const question: DraftQuestion = {
    id: genId(),
    title: q.title,
    body: wireQuestionToBody(q),
  };
  if (q.durationMs !== undefined && q.durationMs > 0) {
    question.durationMs = q.durationMs;
  }
  if (typeof q.image === 'string' && q.image.length > 0) {
    question.image = q.image;
  }
  // Блок кода (Фаза 16, B4): additive-подъём как у image — старые payload
  // без полей дают undefined, черновик остаётся валидным.
  if (typeof q.code === 'string' && q.code.length > 0) {
    question.code = q.code;
    if (typeof q.codeLang === 'string' && q.codeLang.length > 0) {
      question.codeLang = q.codeLang;
    }
  }
  return question;
}

function wireQuestionToBody(q: WireQuestion): DraftQuestionBody {
  // Legacy (Фаза 1): games.payload хранится в БД и отдаётся редактору (GET
  // /api/games/{id}) КАК СЫРАЯ JSON-строка — без нормализации. Старый choice-формат
  // {title, options, correctIndex} НЕ содержит поля "kind": серверный
  // Question.UnmarshalJSON поднимает пустой kind в choice только для движка
  // (проведение/валидация), а parseWireQuizPayload — это голый JSON.parse, поэтому
  // kind остаётся undefined. Без этой ветки switch(q.kind) ниже не совпал бы ни с
  // одним case и функция вернула бы undefined → question.body=undefined → QuestionCard
  // падал на question.body.kind ("Cannot read properties of undefined (reading
  // 'kind')"). Поднимаем choice из top-level options/correctIndex, как на бэкенде.
  if (q.kind === undefined) {
    const legacy = q as WireQuestionBase & { options?: string[]; correctIndex?: number };
    return {
      kind: 'choice',
      options: [...(legacy.options ?? [])],
      correctIndex: legacy.correctIndex ?? 0,
    };
  }
  switch (q.kind) {
    case 'choice':
      return {
        kind: 'choice',
        options: [...q.choice.options],
        correctIndex: q.choice.correctIndex,
      };
    case 'poll':
      return { kind: 'poll', options: [...q.poll.options] };
    case 'open': {
      const body: Extract<DraftQuestionBody, { kind: 'open' }> = {
        kind: 'open',
        accept: [...q.open.accept],
      };
      if (q.open.maxLen !== undefined) {
        body.maxLen = q.open.maxLen;
      }
      return body;
    }
    case 'match':
      return {
        kind: 'match',
        pairs: q.match.pairs.map((p) => ({ left: p.left, right: p.right })),
      };
    case 'order':
      return {
        kind: 'order',
        items: [...q.order.items],
        correctOrder: [...q.order.correctOrder],
      };
    case 'word': {
      const body: Extract<DraftQuestionBody, { kind: 'word' }> = { kind: 'word' };
      if (q.word.maxLen !== undefined) {
        body.maxLen = q.word.maxLen;
      }
      return body;
    }
    case 'multi':
      return {
        kind: 'multi',
        options: [...q.multi.options],
        correct: [...q.multi.correct].sort((a, b) => a - b),
      };
    case 'run':
      return {
        kind: 'run',
        corners: [...q.run.corners],
        correctIndex: q.run.correctIndex,
      };
  }
}

/**
 * Парсит payload библиотечной игры (JSON-строка game.QuizPayload) в WireQuizPayload.
 * Бросает SyntaxError при невалидном JSON. Используется мостом клонирования.
 */
export function parseWireQuizPayload(payload: string): WireQuizPayload {
  return JSON.parse(payload) as WireQuizPayload;
}

// ─── Загрузка изображений (И2: POST /api/uploads) ───────────────────────────────
//
// multipart/form-data, поле `file` (одно изображение); маршрут защищён auth-сессией
// (cookie kikayoo_session отправляется через credentials:'include' в request()).
// Сервер сжимает картинку и возвращает относительный URL вида /uploads/<hex>.jpg,
// который фронт использует как в редакторе (черновик), так и в превью/игре. Поле
// image опционально и хранится в DraftQuestion/WireQuestion/QuestionPayload.
//
// ПОЧЕМУ SVG ЗАПРЕЩЁН (статус 400 от сервера): SVG — это XML, а не растровое
// изображение, и может содержать <script>, обработчики событий (onload=) и внешние
// ссылки (xlink:href). Будучи сохранённым и отданным в origin приложения (тем более
// инлайн или <img> с особенностями парсинга), такой файл становится вектором XSS и
// утечки данных — злоумышленник-ведущий мог бы загрузить «картинку», исполняющую
// скрипт в браузерах участников. Поэтому сервер принимает только растровые форматы
// (JPEG/PNG/WebP/GIF) и сжимает их; SVG (и прочий не-растр) отбрасывается с 400,
// а фронт показывает дружелюбное «недопустимый файл (не SVG)».

/** Тело ответа 201 POST /api/uploads: URL загруженного (и сжатого) изображения. */
interface UploadImageResponse {
  url: string;
}

/**
 * POST /api/uploads (multipart/form-data, поле `file`) → 201 {url}. Загружает одно
 * изображение под auth-сессией и возвращает относительный URL. На ошибку бросает
 * ApiError с HTTP-статусом — caller (HostEditor) маппит статус в дружелюбное
 * i18n-сообщение: 400 (недопустимый файл/SVG), 413 (сырой > 2МБ), 422 (после
 * сжатия слишком большое), 429 (rate-limit), 401/403 (auth/verify).
 *
 * ВАЖНО: Content-Type НЕ задаётся вручную — браузер сам выставит
 * `multipart/form-data; boundary=...` для FormData.
 */
export async function uploadImage(file: File, fetchImpl: typeof fetch = fetch): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await request<UploadImageResponse>(
    '/api/uploads',
    {
      method: 'POST',
      body: form,
    },
    fetchImpl,
  );
  return res.url;
}

// ─── Admin: пользователи (Фаза 7, И1) ─────────────────────────────────────────
//
// Read-only эндпоинты операционного дашборда (GET /api/admin/*). Под
// `RequireAuth + RequireAdmin` на сервере: обычный хост (role='user') получает
// 403 — Admin.tsx маппит статус в дружелюбное i18n-сообщение (admin.forbidden).
// Контракт 1:1 с [admin.go](../server/internal/api/admin.go) DTO (camelCase):
// adminUserItem / adminUserProfile. password_hash НИКОГДА не приходит.

/** Роль пользователя в admin-контексте (как отдаёт /api/admin/users). */
export type AdminRole = 'user' | 'admin';

/**
 * Краткое публичное представление пользователя для списка (GET /api/admin/users).
 * Поля 1:1 с `adminUserItem` backend (camelCase). `blockedAt`/`blockedReason` —
 * опц. (есть только у заблокированных; задел под И2 — карточка block/unblock).
 */
export interface AdminUserItem {
  id: number;
  email: string;
  username: string;
  role: AdminRole;
  isBlocked: boolean;
  verified: boolean;
  createdAt: string;
  blockedAt?: string;
  blockedReason?: string;
}

/**
 * Профиль пользователя + статистика (GET /api/admin/users/{id}). Расширяет
 * AdminUserItem ФИО/updated_at и счётчиками активности (gamesCount/totalScore —
 * агрегаты store.GetUserProfile, sizes игр — И3).
 */
export interface AdminUserProfile extends AdminUserItem {
  fullName?: string;
  updatedAt: string;
  gamesCount: number;
  hostedSessions: number;
  totalPlayers: number;
  totalScore: number;
}

/**
 * Параметры `GET /api/admin/users` (все опциональны). `blocked` — трёхзначная
 * логика: `true` => только заблокированные, `false` => только активные,
 * `undefined` => любая (query не отправляется, сервер не фильтрует).
 */
export interface AdminListUsersParams {
  /** Подстрока поиска по email/username (LIKE %q%). */
  q?: string;
  /** Фильтр по роли ('admin' | 'user'); пусто => любая. */
  role?: AdminRole | '';
  /** Фильтр по блокировке (true/false/не задан). */
  blocked?: boolean;
  /** Размер страницы (сервер кэпа к defaultListUsersLimit при <=0 или >max). */
  limit?: number;
  /** Сдвиг страницы (>=0). */
  offset?: number;
  /** Инъекция fetch для тестов. */
  fetchImpl?: typeof fetch;
}

/**
 * GET /api/admin/users[?q=&role=&blocked=&limit=&offset=] → 200 AdminUserItem[].
 * 401 — без auth; 403 — не админ. `fetchImpl` инъектируется для тестов.
 */
export async function adminListUsers(params: AdminListUsersParams = {}): Promise<AdminUserItem[]> {
  const { q, role, blocked, limit, offset, fetchImpl = fetch } = params;
  const query = new URLSearchParams();
  if (q && q.length > 0) query.set('q', q);
  if (role && role.length > 0) query.set('role', role);
  if (blocked !== undefined) query.set('blocked', String(blocked));
  if (limit !== undefined && limit > 0) query.set('limit', String(limit));
  if (offset !== undefined && offset > 0) query.set('offset', String(offset));
  const path = query.toString().length > 0 ? `/api/admin/users?${query}` : '/api/admin/users';
  const users = await request<AdminUserItem[] | null>(path, { method: 'GET' }, fetchImpl);
  return users ?? [];
}

/**
 * GET /api/admin/users/{id} → 200 AdminUserProfile (детали + статистика).
 * 400 — невалидный id; 401 — без auth; 403 — не админ; 404 — не найден.
 */
export function adminGetUser(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminUserProfile> {
  return request<AdminUserProfile>(`/api/admin/users/${id}`, { method: 'GET' }, fetchImpl);
}

// ─── Admin: мутации аккаунтов (Фаза 7, И2) ───────────────────────────────────
//
// block/unblock/role/verify/logout-all под RequireAuth+RequireAdmin. Контракт 1:1
// с [admin.go](../server/internal/api/admin.go) (camelCase DTO). Каждая мутация
// возвращает обновлённый DTO юзера (adminUserItem — без password_hash); на не-2xx
// `request` бросает ApiError, где `.status` — HTTP-код, а `.message` — код ошибки
// из тела `{"error":"<код>"}` (last_admin / cannot_block_self / cannot_revoke_self
// для 409-гвардов RA1). Admin.tsx маппит эти коды в человекочитаемые i18n-сообщения.

/**
 * POST /api/admin/users/{id}/block {reason?} → 200 AdminUserItem (полный бан,
 * вариант A). reason опциональна (показывается нарушителю + попадает в audit.meta).
 * 409 cannot_block_self (actor==target) | last_admin (единственный админ).
 */
export async function adminBlockUser(
  id: number,
  reason = '',
  fetchImpl: typeof fetch = fetch,
): Promise<AdminUserItem> {
  return request<AdminUserItem>(
    `/api/admin/users/${id}/block`,
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ reason }) },
    fetchImpl,
  );
}

/**
 * POST /api/admin/users/{id}/unblock → 200 AdminUserItem (снятие блокировки).
 * Гвардов нет: разблокировка всегда безопасна. 404 — нет юзера.
 */
export async function adminUnblockUser(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminUserItem> {
  return request<AdminUserItem>(`/api/admin/users/${id}/unblock`, { method: 'POST' }, fetchImpl);
}

/**
 * POST /api/admin/users/{id}/role {role} → 200 AdminUserItem. role='admin' (grant)
 * | 'user' (revoke). Идемпотентна (роль не изменилась → no-op без audit).
 * 400 — невалидная роль; 409 cannot_revoke_self (снять с себя) | last_admin.
 */
export async function adminSetUserRole(
  id: number,
  role: AdminRole,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminUserItem> {
  return request<AdminUserItem>(
    `/api/admin/users/${id}/role`,
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ role }) },
    fetchImpl,
  );
}

/**
 * POST /api/admin/users/{id}/verify → 200 AdminUserItem (ручная верификация email;
 * поддержка при потере токена/недоступности SMTP). 404 — нет юзера.
 */
export async function adminVerifyUser(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminUserItem> {
  return request<AdminUserItem>(`/api/admin/users/${id}/verify`, { method: 'POST' }, fetchImpl);
}

/**
 * Ответ POST /api/admin/users/{id}/logout-all: обновлённый DTO юзера + число
 * сброшенных server-side сессий (sessionsDeleted). Расширяет AdminUserItem.
 */
export interface AdminLogoutAllResponse extends AdminUserItem {
  /** Число отозванных сессий (для подтверждения админу). */
  sessionsDeleted: number;
}

/**
 * POST /api/admin/users/{id}/logout-all → 200 AdminLogoutAllResponse (отзыв всех
 * сессий пользователя). 404 — нет юзера.
 */
export async function adminLogoutAll(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminLogoutAllResponse> {
  return request<AdminLogoutAllResponse>(
    `/api/admin/users/${id}/logout-all`,
    { method: 'POST' },
    fetchImpl,
  );
}

// ─── Admin: аудит (Фаза 7, И2, read-only) ────────────────────────────────────
//
// GET /api/admin/audit?action=&type=&actor=&target=&limit=&offset= — лента
// admin-мутаций от новых к старым. Контракт 1:1 с adminAuditItem (camelCase):
// actorEmail backend НЕ отдаёт (см. admin.go) — UI показывает actorUserId.

/**
 * Канонические коды действий аудита (admin_actions.action). Совпадают с константами
 * store.Action*; используются для i18n-маппинга в человекочитаемые метки.
 */
export const AUDIT_ACTION = {
  block: 'user.block',
  unblock: 'user.unblock',
  roleGrant: 'user.role.grant',
  roleRevoke: 'user.role.revoke',
  verify: 'user.verify',
  logoutAll: 'user.logout_all',
  // И4: правка контента публичных витрин (PUT /api/admin/showcase/{id}).
  showcaseUpdate: 'showcase.update',
  // И5: модерация картинок — удаление файла из UPLOADS_DIR (POST /api/admin/uploads/{name}/delete).
  uploadDelete: 'upload.delete',
  // И6: правка конфигурируемых лимитов (PUT /api/admin/limits).
  limitsUpdate: 'limits.update',
} as const;

/** Тип объекта аудита (admin_actions.target_type): 'user' (мутации аккаунтов) | 'game' (showcase). */
export const AUDIT_TARGET_USER = 'user';
/** Тип объекта аудита — игра (showcase.update, game.delete; И3/И4). */
export const AUDIT_TARGET_GAME = 'game';

/**
 * Одна запись аудита (GET /api/admin/audit). Поля 1:1 с `adminAuditItem` backend
 * (camelCase). `targetId`/`meta` опциональны (omitempty на сервере); `meta` —
 * встроенный JSON-объект (блок → {reason}, роль → {role}, logout-all → {deleted}).
 */
export interface AdminAuditItem {
  id: number;
  actorUserId: number;
  action: string;
  targetType: string;
  targetId?: number;
  meta?: Record<string, unknown>;
  createdAt: string;
}

/** Параметры GET /api/admin/audit (все опциональны). */
export interface AdminListAuditParams {
  /** Фильтр по коду действия (точное совпадение; '' => любой). */
  action?: string;
  /** Фильтр по типу объекта (target_type; обычно 'user'). */
  type?: string;
  /** Фильтр по исполнителю (actor_user_id; >0). */
  actor?: number;
  /** Фильтр по id объекта (target_id; >0). */
  target?: number;
  /** Размер страницы (сервер кэпа к defaultListUsersLimit при <=0 или >max). */
  limit?: number;
  /** Сдвиг страницы (>=0). */
  offset?: number;
  /** Инъекция fetch для тестов. */
  fetchImpl?: typeof fetch;
}

/**
 * GET /api/admin/audit[?action=&type=&actor=&target=&limit=&offset=] →
 * 200 AdminAuditItem[] (от новых к старым). 401 — без auth; 403 — не админ.
 */
export async function adminListAudit(params: AdminListAuditParams = {}): Promise<AdminAuditItem[]> {
  const { action, type, actor, target, limit, offset, fetchImpl = fetch } = params;
  const query = new URLSearchParams();
  if (action && action.length > 0) query.set('action', action);
  if (type && type.length > 0) query.set('type', type);
  if (actor && actor > 0) query.set('actor', String(actor));
  if (target && target > 0) query.set('target', String(target));
  if (limit !== undefined && limit > 0) query.set('limit', String(limit));
  if (offset !== undefined && offset > 0) query.set('offset', String(offset));
  const path = query.toString().length > 0 ? `/api/admin/audit?${query}` : '/api/admin/audit';
  const items = await request<AdminAuditItem[] | null>(path, { method: 'GET' }, fetchImpl);
  return items ?? [];
}

// ─── Admin: дашборд игр (Фаза 7, И3, план §2.6/§4) ──────────────────────────
//
// GET /api/admin/games (все игры + фильтр type/category/owner/q + РАЗМЕРЫ:
// payloadBytes/imagesBytes/totalBytes через game.GameSize); GET /api/admin/users/{id}/games
// (игры хоста); DELETE /api/admin/games/{id} (удаление ЛЮБОГО квиза, admin override без
// owner-check + audit game.delete). Контракт 1:1 с [admin.go](../server/internal/api/admin.go)
// DTO adminGameItem (camelCase). OwnerEmail/Username/Category/OwnerUserId — опц. (omitempty:
// пусты для library-seed/anon/удалённого владельца). На не-2xx `request` бросает ApiError,
// где .status — HTTP-код, а .message — код из тела `{"error":...}`.

/**
 * Игра для admin-дашборда: метаданные + размеры (без payload — он тяжёлый для списка).
 * Поля 1:1 с `adminGameItem` backend (camelCase). category/ownerUserId/ownerEmail/
 * ownerUsername опускаются сервером при NULL (личные квизы/anon/library-seed).
 */
export interface AdminGameItem {
  id: number;
  type: string;
  /** Категория внутри library ('showcase' | 'questions'); опц. для личных квизов. */
  category?: string;
  title: string;
  /** ID владельца (NULL для library-seed/anon). */
  ownerUserId?: number;
  /** Email владельца из LEFT JOIN users (пусто для library/anon/удалённого). */
  ownerEmail?: string;
  /** Ник владельца (как ownerEmail — опц.). */
  ownerUsername?: string;
  createdAt: string;
  /** Размер JSON-payload квиза (len(payload), байты). */
  payloadBytes: number;
  /** Сумма os.Stat по Question.Image (байты); 0 если картинок нет/uploadsDir не задан. */
  imagesBytes: number;
  /** payloadBytes + imagesBytes — полный «вес» квиза. */
  totalBytes: number;
  /** Число вопросов с изображением. */
  imageCount: number;
}

/** Параметры `GET /api/admin/games` (все опциональны). */
export interface AdminListGamesParams {
  /** Подстрока поиска по title (LIKE %q%). */
  q?: string;
  /** Фильтр по типу ('quiz' | 'library'); пусто => любой. */
  type?: string;
  /** Фильтр по category внутри library; пусто => любой. */
  category?: string;
  /** Фильтр по владельцу (owner_user_id; >0). */
  owner?: number;
  /** Размер страницы (сервер кэпа к defaultListUsersLimit при <=0 или >max). */
  limit?: number;
  /** Сдвиг страницы (>=0). */
  offset?: number;
  /** Инъекция fetch для тестов. */
  fetchImpl?: typeof fetch;
}

/**
 * GET /api/admin/games[?q=&type=&category=&owner=&limit=&offset=] → 200 AdminGameItem[]
 * (все игры системы с размерами). 401 — без auth; 403 — не админ. `fetchImpl` для тестов.
 */
export async function adminListGames(params: AdminListGamesParams = {}): Promise<AdminGameItem[]> {
  const { q, type, category, owner, limit, offset, fetchImpl = fetch } = params;
  const query = new URLSearchParams();
  if (q && q.length > 0) query.set('q', q);
  if (type && type.length > 0) query.set('type', type);
  if (category && category.length > 0) query.set('category', category);
  if (owner && owner > 0) query.set('owner', String(owner));
  if (limit !== undefined && limit > 0) query.set('limit', String(limit));
  if (offset !== undefined && offset > 0) query.set('offset', String(offset));
  const path = query.toString().length > 0 ? `/api/admin/games?${query}` : '/api/admin/games';
  const games = await request<AdminGameItem[] | null>(path, { method: 'GET' }, fetchImpl);
  return games ?? [];
}

/** Параметры GET /api/admin/users/{id}/games (только пагинация; owner фиксирован = id). */
export interface AdminListUserGamesParams {
  /** Размер страницы. */
  limit?: number;
  /** Сдвиг страницы (>=0). */
  offset?: number;
  /** Инъекция fetch для тестов. */
  fetchImpl?: typeof fetch;
}

/**
 * GET /api/admin/users/{id}/games → 200 AdminGameItem[] (игры конкретного хоста с размерами).
 * 400 — невалидный id; 404 — пользователь не найден; 401 — без auth; 403 — не админ.
 */
export async function adminListUserGames(
  id: number,
  params: AdminListUserGamesParams = {},
): Promise<AdminGameItem[]> {
  const { limit, offset, fetchImpl = fetch } = params;
  const query = new URLSearchParams();
  if (limit !== undefined && limit > 0) query.set('limit', String(limit));
  if (offset !== undefined && offset > 0) query.set('offset', String(offset));
  const qs = query.toString();
  const path =
    qs.length > 0 ? `/api/admin/users/${id}/games?${qs}` : `/api/admin/users/${id}/games`;
  const games = await request<AdminGameItem[] | null>(path, { method: 'GET' }, fetchImpl);
  return games ?? [];
}

/** Ответ DELETE /api/admin/games/{id}: {deleted: id} (200). */
export interface AdminDeleteGameResponse {
  deleted: number;
}

/**
 * DELETE /api/admin/games/{id} → 200 {deleted: id} (удаление ЛЮБОГО квиза, admin override
 * без owner-check + audit game.delete). 404 — игры нет (уже удалена); 401/403 — gate.
 */
export function adminDeleteGame(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminDeleteGameResponse> {
  return request<AdminDeleteGameResponse>(
    `/api/admin/games/${id}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

// ─── Admin: дашборд активных сессий (Фаза 7, И3, план §2.7/§2.8) ─────────────
//
// GET /api/admin/sessions/active — снимок живых комнат (hub.RoomInfo); GET /api/admin/
// sessions/{id} — детали (active из Snapshot / неактивна из БД); POST /api/admin/sessions/
// {id}/stop — принудительная остановка (Hub.StopSession + audit session.stop). Контракт 1:1
// с [admin.go](../server/internal/api/admin.go) и [hub.go](../server/internal/hub/hub.go)
// RoomInfo/adminSessionResp (camelCase JSON-теги).

/**
 * Активная комната из Hub.Snapshot (GET /api/admin/sessions/active). Поля 1:1 с
 * `hub.RoomInfo` (camelCase). hostUserId/currentQuestionId опц. (omitempty: 0 для
 * анонимной сессии / lobby+finished).
 */
export interface AdminRoomInfo {
  pin: string;
  gameId: number;
  sessionId: number;
  /** ID хоста-владельца аккаунта; 0/опущен для анонимной сессии (library без аккаунта). */
  hostUserId?: number;
  playerCount: number;
  /** Текущий вопрос; опц. (0 в lobby/finished → опускается). */
  currentQuestionId?: number;
  /** Фаза движка: lobby/question/reveal/leaderboard/finished. */
  phase: string;
  /** ISO8601 UTC — момент создания комнаты в реестре. */
  startedAt: string;
}

/**
 * GET /api/admin/sessions/active → 200 AdminRoomInfo[] (живые комнаты, возможно пустой).
 * 401 — без auth; 403 — не админ. Нагрузка низкая (admin-дашборд, polling).
 */
export async function adminListActiveSessions(
  fetchImpl: typeof fetch = fetch,
): Promise<AdminRoomInfo[]> {
  const sessions = await request<AdminRoomInfo[] | null>(
    '/api/admin/sessions/active',
    { method: 'GET' },
    fetchImpl,
  );
  return sessions ?? [];
}

/**
 * Детали одной сессии (GET /api/admin/sessions/{id}). Active=true — живая комната
 * (метаданные из Snapshot); Active=false — завершённая/неактивная (БД-запись sessions:
 * host, finishedAt). Поля живой комнаты опускаются (omitempty) для неактивной, и наоборот.
 */
export interface AdminActiveSession {
  active: boolean;
  pin?: string;
  gameId: number;
  sessionId: number;
  hostUserId?: number;
  playerCount?: number;
  currentQuestionId?: number;
  phase?: string;
  startedAt: string;
  /** Ник хоста (для неактивной БД-сессии). */
  host?: string;
  finishedAt?: string;
}

/**
 * GET /api/admin/sessions/{id} → 200 AdminActiveSession (детали + статистика фаз).
 * 400 — невалидный id; 404 — сессии нет; 401/403 — gate.
 */
export function adminGetSession(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminActiveSession> {
  return request<AdminActiveSession>(`/api/admin/sessions/${id}`, { method: 'GET' }, fetchImpl);
}

/** Ответ POST /api/admin/sessions/{id}/stop: {stopped: id} (200). */
export interface AdminStopSessionResponse {
  stopped: number;
}

/**
 * POST /api/admin/sessions/{id}/stop → 200 {stopped: id} (принудительная остановка:
 * закрыть комнату, разослать WS finished, пометить finished_at + audit session.stop).
 * 404 — сессия не активна (уже завершена/нет); 401/403 — gate; 500 — внутр. ошибка.
 */
export function adminStopSession(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminStopSessionResponse> {
  return request<AdminStopSessionResponse>(
    `/api/admin/sessions/${id}/stop`,
    { method: 'POST' },
    fetchImpl,
  );
}

// ─── Admin: редактор витрин (Фаза 7, И4, план §4) ───────────────────────────
//
// GET/PUT /api/admin/showcase/{id} — правка контента публичных витрин
// (type='library' AND category='showcase': Космос/Звёздные войны/Наука и т.п.).
// Guard на сервере: НЕ showcase → 409 not_showcase. Список витчин берётся из
// adminListGames({type:'library', category:'showcase'}) (И3); payload НЕ входит в
// список (тяжёлый), поэтому редактор грузит его отдельным GET перед правкой.
//
// ВАЖНО: payload отдаётся РАВЫМ (без подписи secure_link) — редактор правит
// канонический games.payload, как он хранится в БД; подпись навешивается сервером
// только при отдаче клиентам. PUT валидирует payload тем же game.ParseQuizPayload,
// что создание/правка квиза (→ 400 на невалидный).

/**
 * Showcase-игра для редактора: метаданные + РАВЫЙ payload (без подписи secure_link)
 * + размер payload. Поля 1:1 с `showcaseDetail` backend (camelCase). category
 * всегда 'showcase' (guard на сервере).
 */
export interface AdminShowcaseDetail extends GameMeta {
  /** JSON-строка game.QuizPayload, как хранится в БД (с «голыми» путями картинок). */
  payload: string;
  /** len(payload), байты — «вес» контента витрины. */
  payloadBytes: number;
}

/** Тело PUT /api/admin/showcase/{id}: частичное обновление (title и/или payload). */
export interface AdminUpdateShowcaseBody {
  /** Новое название; при наличии не должно быть пустым (→ 400). */
  title?: string;
  /** JSON-строка wire-формата квиза; опц., валидируется сервером (→ 400). */
  payload?: string;
}

/**
 * GET /api/admin/showcase/{id} → 200 AdminShowcaseDetail (равый payload для
 * редактора). 400 — невалидный id; 404 — игры нет; 409 — not_showcase; 401/403 — gate.
 */
export function adminGetShowcase(
  id: number,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminShowcaseDetail> {
  return request<AdminShowcaseDetail>(`/api/admin/showcase/${id}`, { method: 'GET' }, fetchImpl);
}

/**
 * PUT /api/admin/showcase/{id} {title?, payload?} → 200 AdminShowcaseDetail
 * (обновлённый контент + audit showcase.update в той же tx на сервере). Guard: не
 * showcase → 409 not_showcase; невалидный payload/пустой title → 400; нет игры → 404.
 */
export async function adminUpdateShowcase(
  id: number,
  body: AdminUpdateShowcaseBody,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminShowcaseDetail> {
  return request<AdminShowcaseDetail>(
    `/api/admin/showcase/${id}`,
    { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body) },
    fetchImpl,
  );
}

// ─── Admin: модерация картинок (Фаза 7, И5, план §4) ────────────────────────
//
// GET  /api/admin/uploads                — список файлов UPLOADS_DIR с размерами,
//   mtime и referenced-флагом (есть ли квиз, ссылающийся на файл); фильтры
//   ?referenced=1|0 и ?q=<name>, пагинация ?limit/&offset, сортировка mtime desc.
// POST /api/admin/uploads/{name}/delete  — удаление файла. Path-traversal guard на
//   сервере (только basename .webp/.jpg внутри uploadsDir) → 400 на обход. 404 если
//   файла нет. 409 {error:"referenced", games:[ids]} если на файл ссылается квиз и
//   НЕ передан ?force=1; ?force=1 (или unreferenced) → 200 {deleted:name} + audit.
//
// На не-2xx `request` бросает ApiError, где .status — HTTP-код, .message — код из
// `{"error":...}`, а .body — полный объект (нужен для чтения games[] из 409 referenced).

/**
 * Один файл UPLOADS_DIR для списка модерации (И5). Поля 1:1 с `adminUploadItem`
 * backend (camelCase). name — basename (без пути); referenced=true, если хотя бы
 * один квиз ссылается на файл (Question.Image = "/uploads/<name>").
 */
export interface AdminUploadItem {
  name: string;
  /** Размер файла, байты. */
  size: number;
  /** UTC ISO8601 времени модификации файла. */
  mtime: string;
  /** true, если хотя бы один квиз ссылается на файл (защита от битых <img>, RA7). */
  referenced: boolean;
}

/** Ответ GET /api/admin/uploads: страница элементов + total (до пагинации). */
export interface AdminListUploadsResp {
  items: AdminUploadItem[];
  /** Общее число файлов по фильтру (для пагинации/«показано N из M»). */
  total: number;
}

/** Параметры `GET /api/admin/uploads` (все опциональны). */
export interface AdminListUploadsParams {
  /** Фильтр по referenced: true → только используемые, false → только свободные. */
  referenced?: boolean;
  /** Подстрока поиска по имени файла (case-insensitive). */
  q?: string;
  /** Размер страницы (сервер кэпа к default/max при <=0 или >max). */
  limit?: number;
  /** Сдвиг страницы (>=0). */
  offset?: number;
  /** Инъекция fetch для тестов. */
  fetchImpl?: typeof fetch;
}

/** Результат успешного удаления: {deleted: name}. */
export interface AdminDeleteUploadResult {
  /** Имя удалённого файла (подтверждение). */
  deleted: string;
}

/**
 * GET /api/admin/uploads[?referenced=&q=&limit=&offset=] → 200 AdminListUploadsResp
 * ({items:[{name,size,mtime,referenced}, ...], total}). 401 — без auth; 403 — не админ;
 * 503 — uploads не настроены. `fetchImpl` для тестов.
 */
export async function adminListUploads(
  params: AdminListUploadsParams = {},
): Promise<AdminListUploadsResp> {
  const { referenced, q, limit, offset, fetchImpl = fetch } = params;
  const query = new URLSearchParams();
  if (referenced !== undefined) query.set('referenced', referenced ? '1' : '0');
  if (q && q.length > 0) query.set('q', q);
  if (limit !== undefined && limit > 0) query.set('limit', String(limit));
  if (offset !== undefined && offset > 0) query.set('offset', String(offset));
  const path = query.toString().length > 0 ? `/api/admin/uploads?${query}` : '/api/admin/uploads';
  const resp = await request<AdminListUploadsResp | null>(path, { method: 'GET' }, fetchImpl);
  // Сервер всегда отдаёт {items,total} (items — [] при пустоте, не null), но на всякий
  // случай нормализуем null/отсутствие items до пустого массива.
  if (resp && Array.isArray(resp.items)) return resp;
  return { items: [], total: resp?.total ?? 0 };
}

/**
 * POST /api/admin/uploads/{name}/delete[?force=1] → 200 {deleted:name} (audit
 * upload.delete пишется на сервере). name кодируется через encodeURIComponent (basename
 * файла; серверный path-traversal guard всё равно отклонит ".."/"/"/чужое расширение).
 *
 * force=true передаётся как ?force=1 и разрешает удаление referenced-картинки (квиз(ы)
 * после этого покажут битый <img> — осознанное действие админа). При 409 referenced
 * (force=false, файл используется) `request` бросает ApiError(409, "referenced"), чьё
 * `.body.games` содержит id ссылающихся квизов — UI показывает их для проверки.
 *
 * 200 — AdminDeleteUploadResult; 400 — невалидное name; 404 — файла нет; 409 —
 * referenced без force (.body.games — список id); 401/403 — gate; 500 — ФС/БД.
 */
export async function adminDeleteUpload(
  name: string,
  opts: { force?: boolean; fetchImpl?: typeof fetch } = {},
): Promise<AdminDeleteUploadResult> {
  const { force = false, fetchImpl = fetch } = opts;
  const path = force
    ? `/api/admin/uploads/${encodeURIComponent(name)}/delete?force=1`
    : `/api/admin/uploads/${encodeURIComponent(name)}/delete`;
  return request<AdminDeleteUploadResult>(path, { method: 'POST' }, fetchImpl);
}

// ─── Admin: конфигурируемые лимиты (Фаза 7, И6, план §2.10.6) ───────────────
//
// GET  /api/admin/limits        — эффективные значения + метаданные (default/
//   min/max/description) всех 9 лимитов; для рендера вкладки «Лимиты» (Admin).
// PUT  /api/admin/limits        — частичное обновление {limits:{key:value,...}}
//   (только ключи из allowlist limits.*; значения — строки-числа; валидация int +
//   диапазон на сервере); пишет audit limits.update в той же tx и инвалидирует
//   кеш (последующий GET/public видит новое значение).
//
// Контракты: на 2xx `request` парсит JSON; на 4xx/5xx — ApiError, где .status —
// HTTP-код, .message — код из `{"error":...}` (400 invalid/out_of_range/unknown).
// 401/403 — gate RequireAuth+RequireAdmin (для user недоступно).

/**
 * Ключи конфигурируемых лимитов (1:1 с store.Setting*). Строковые константы (в
 * settings они хранятся как key-value TEXT). Экспортируются для UI-маппинга и
 * построения тела PUT (ключи — строки, значения — строки-числа, как ждёт сервер).
 */
export const LIMIT_KEYS = {
  quizzesPerHost: 'limits.quizzes_per_host',
  playersPerRoom: 'limits.players_per_room',
  parallelRooms: 'limits.parallel_rooms',
  quizImageTotalBytes: 'limits.quiz_image_total_bytes',
  questionsPerQuiz: 'limits.questions_per_quiz',
  charQuizTitle: 'limits.char_quiz_title',
  charQuestion: 'limits.char_question',
  charOption: 'limits.char_option',
  charOpenAnswer: 'limits.char_open_answer',
  openQuizzesPerUser: 'limits.open_quizzes_per_user',
  openQuizMaxAttempts: 'limits.open_quiz_max_attempts',
  roomIdleTTLMinutes: 'limits.room_idle_ttl_minutes',
  autoNextDelaySeconds: 'limits.auto_next_delay_seconds',
  codeMaxChars: 'limits.code_max_chars',
} as const;

/**
 * Один лимит в ответе GET /api/admin/limits (adminLimitItem backend, camelCase).
 * value — эффективное значение (БД ?? default); default — hardcoded fallback;
 * min/max — границы валидации при PUT; description — человекочитаемое (en) для UI.
 */
export interface AdminLimitItem {
  key: string;
  value: string;
  default: string;
  min: number;
  max: number;
  description: string;
}

/** Ответ GET /api/admin/limits: стабильный список AdminLimitItem (11 ключей). */
export interface AdminLimitsResp {
  limits: AdminLimitItem[];
}

/** Тело PUT /api/admin/limits: частичное обновление, значения — строки-числа. */
export type AdminUpdateLimitsBody = { [key: string]: string };

/**
 * GET /api/admin/limits → 200 AdminLimitsResp (9 элементов с value+метаданными).
 * 401/403 — gate RequireAuth+RequireAdmin (обычный хост недоступен).
 */
export function adminGetLimits(fetchImpl: typeof fetch = fetch): Promise<AdminLimitsResp> {
  return request<AdminLimitsResp>('/api/admin/limits', { method: 'GET' }, fetchImpl);
}

/**
 * PUT /api/admin/limits {limits:{key:value,...}} → 200 AdminLimitsResp (обновлённый
 * полный набор — UI сразу видит результат). Только ключи из allowlist limits.*
 * (см. LIMIT_KEYS); значение — строка-число в диапазоне [min,max]. Сервер пишет
 * audit limits.update в той же tx и инвалидирует кеш. 400 — невалидный ключ/
 * не-число/out of range; 401/403 — gate.
 */
export function adminUpdateLimits(
  limits: AdminUpdateLimitsBody,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminLimitsResp> {
  return request<AdminLimitsResp>(
    '/api/admin/limits',
    { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ limits }) },
    fetchImpl,
  );
}

// ─── Public лимиты (GET /api/limits, user-facing счётчики) ───────────────────
//
// GET /api/limits (public, под LoadSession, НЕ RequireAdmin) — camelCase-числовое
// подмножество лимитов для счётчиков Dashboard/HostEditor. Анониму — глобальные
// дефолты; залогиненному — EffectiveLimit (per-user override пуст в v1). Значения
// — числа (UI сравнивает N/limit числами, без парсинга строк, как у /api/admin).

/**
 * Эффективные лимиты для user-facing счётчиков (GET /api/limits). Все 9 полей —
 * числа, camelCase (1:1 с publicLimitsResp backend). quizzesPerHost — лимит
 * квизов на ведущего (Dashboard «N/limit»); questionsPerQuiz — лимит вопросов
 * (HostEditor «N/20»); quizImageTotalBytes — суммарный размер картинок в квизе;
 * char* — maxLength текстовых полей (превентивная подсветка в редакторе).
 */
export interface EffectiveLimits {
  quizzesPerHost: number;
  playersPerRoom: number;
  parallelRooms: number;
  quizImageTotalBytes: number;
  questionsPerQuiz: number;
  charQuizTitle: number;
  charQuestion: number;
  charOption: number;
  charOpenAnswer: number;
  /** Лимит одновременно опубликованных открытых квизов (Фаза 10, И12). */
  openQuizzesPerUser: number;
  /** Лимит соло-попыток на опубликованный открытый квиз (Фаза 10, И12). */
  openQuizMaxAttempts: number;
  /** Время жизни пустой комнаты в минутах (настройка админа). */
  roomIdleTTLMinutes: number;
  /** Максимальная длина блока кода вопроса в рунах (Фаза 16, B1; дефолт 2000). */
  codeMaxChars: number;
}

/**
 * GET /api/limits → 200 EffectiveLimits (camelCase числа). Public — без auth
 * (LoadSession): аноним получает глобальные дефолты, залогиненный — свои лимиты
 * (forward-compat под тарифы). Никогда не падает из-за отсутствия строки (есть
 * hardcoded fallback). 500 — только при ошибке БД (практически невозможна).
 */
export function getEffectiveLimits(fetchImpl: typeof fetch = fetch): Promise<EffectiveLimits> {
  return request<EffectiveLimits>('/api/limits', { method: 'GET' }, fetchImpl);
}

// ─── Открытые квизы (Фаза 10, Секция C, И11–И12) ─────────────────────────────
//
// Публичный соло-флоу (server/internal/api/open_quizzes.go): GET /api/open/{slug}
// (мета без вопросов), POST /api/open/{slug}/attempts (старт с ником → токен
// попытки + вопросы БЕЗ правильных ответов — анти-чит), POST
// /api/open/attempts/{id}/{answers,finish,state} с заголовком X-Attempt-Token
// (не в query — не течёт в access-логи). Reveal приходит ТОЛЬКО с finish.
//
// Владельческие обёртки (me_open.go): publish/unpublish/status/history/detail/
// stats/reset в группе /api/me (cookie-auth), ownership-404 на чужое.

/** Мета открытого квиза (GET /api/open/{slug}): вопросы НЕ отдаются без ника. */
export interface OpenQuizMeta {
  slug: string;
  title: string;
  questionCount: number;
  publishedAt: string;
  /** open | closed (лимит попыток исчерпан). */
  status: 'open' | 'closed';
  /** "attempts_limit" при closed; null при открытом. */
  closedReason: string | null;
}

/** kindData-дискриминатор вопроса попытки (json.RawMessage на сервере). */
export type OpenKindData = Record<string, unknown>;

/**
 * Вопрос в ответе старта попытки: kindData строится только из KindData() —
 * choice/poll {options[]}, open/word {maxLen}, match {left[],right[]},
 * order {items[]}. Правильных ответов в DTO физически нет (анти-чит).
 */
export interface OpenQuestion {
  questionId: number;
  kind: string;
  title: string;
  /** Подписанный secure_link URL (может отсутствовать). */
  image?: string;
  /**
   * Блок кода вопроса (Фаза 16, B1/B3, additive): код — не секрет, в открытых
   * квизах отдаётся как текст. Optional: отсутствует, пока серверная DTO
   * открытых попыток не пробросила поля (WS-канал уже несёт).
   */
  code?: string;
  /** Язык блока кода из whitelist (отсутствует/пусто => plaintext). */
  codeLang?: string;
  kindData?: OpenKindData;
}

/** Ответ POST /api/open/{slug}/attempts: токен + deck вопросов. */
export interface StartOpenAttemptResp {
  attemptId: number;
  /** Секрет попытки; далее в заголовке X-Attempt-Token на каждый call. */
  token: string;
  totalQuestions: number;
  questions: OpenQuestion[];
}

/**
 * Тело сабмита ответа (форма WS AnswerPayload): choice/poll → {option};
 * open → {data:{text}}; match → {data:{pairs}}; order → {data:{order}};
 * word → {data:{word}}.
 */
export interface OpenAnswerBody {
  questionId: number;
  option?: number;
  data?: Record<string, unknown>;
}

/** Ответ сабмита: только факт приёма, correct НЕ возвращается до финиша. */
export interface OpenAnswerResp {
  accepted: boolean;
  answered: number;
}

/** Reveal-данные финиша (json.RawMessage на сервере; poll/word — нет). */
export type OpenRevealData = Record<string, unknown>;

/** Строка reveal-итога финиша: verdict + очки + правильный ответ. */
export interface OpenFinishQuestion {
  questionId: number;
  correct: boolean;
  points: number;
  reveal?: OpenRevealData;
}

/** Ответ POST finish: ЕДИНСТВЕННОЕ место с reveal (§C4.1). */
export interface OpenFinishResp {
  nickname: string;
  score: number;
  answerCount: number;
  correctCount: number;
  totalQuestions: number;
  questions: OpenFinishQuestion[];
}

/** Ответ POST state (resume): статус + отвеченные questionId. */
export interface OpenAttemptState {
  status: 'active' | 'finished' | 'abandoned';
  answeredQuestionIds: number[];
}

/** Заголовок токена попытки (X-Attempt-Token) для answers/finish/state. */
export const ATTEMPT_TOKEN_HEADER = 'X-Attempt-Token';

/** GET /api/open/{slug} → 200 OpenQuizMeta; 404 — нет такого/снята публикация. */
export function fetchOpenQuizMeta(
  slug: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenQuizMeta> {
  return request<OpenQuizMeta>(
    `/api/open/${encodeURIComponent(slug)}`,
    { method: 'GET' },
    fetchImpl,
  );
}

/**
 * POST /api/open/{slug}/attempts {nickname} → 201 StartOpenAttemptResp.
 * 400 nickname_required; 404 slug; 409 quiz_closed; 429 rate-limit (30/ч на IP).
 */
export function startOpenAttempt(
  slug: string,
  nickname: string,
  fetchImpl: typeof fetch = fetch,
): Promise<StartOpenAttemptResp> {
  return request<StartOpenAttemptResp>(
    `/api/open/${encodeURIComponent(slug)}/attempts`,
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ nickname }) },
    fetchImpl,
  );
}

/**
 * POST /api/open/attempts/{id}/answers (X-Attempt-Token) → 200 {accepted,answered}
 * БЕЗ correct. Идемпотентно (PK attempt+question: первый ответ засчитан).
 * 400 невалидный ответ; 401 токен; 404 попытки/квиза нет; 409 attempt_finished.
 */
export function submitOpenAnswer(
  attemptId: number,
  token: string,
  body: OpenAnswerBody,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenAnswerResp> {
  return request<OpenAnswerResp>(
    `/api/open/attempts/${attemptId}/answers`,
    {
      method: 'POST',
      headers: { ...JSON_HEADERS, [ATTEMPT_TOKEN_HEADER]: token },
      body: JSON.stringify(body),
    },
    fetchImpl,
  );
}

/**
 * POST /api/open/attempts/{id}/finish (X-Attempt-Token) → 200 OpenFinishResp
 * (итоги + reveal всех вопросов). Повторный вызов идемпотентен.
 */
export function finishOpenAttempt(
  attemptId: number,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenFinishResp> {
  return request<OpenFinishResp>(
    `/api/open/attempts/${attemptId}/finish`,
    { method: 'POST', headers: { [ATTEMPT_TOKEN_HEADER]: token } },
    fetchImpl,
  );
}

/**
 * POST /api/open/attempts/{id}/state (X-Attempt-Token) → 200 OpenAttemptState
 * (resume: status + answeredQuestionIds; вопросы НЕ пере-выдаются — deck у клиента
 * в sessionStorage).
 */
export function fetchOpenAttemptState(
  attemptId: number,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenAttemptState> {
  return request<OpenAttemptState>(
    `/api/open/attempts/${attemptId}/state`,
    { method: 'POST', headers: { [ATTEMPT_TOKEN_HEADER]: token } },
    fetchImpl,
  );
}

// ─── Открытые квизы: владельческие (группа /api/me, И12) ─────────────────────

/** Ответ POST /api/me/games/{id}/open/publish: slug + полная ссылка (QR/копия). */
export interface OpenPublishResp {
  slug: string;
  /** Полная ссылка {origin}/q/<slug> (собрана сервером из request-host). */
  url: string;
  publishedAt: string;
}

/** Ответ GET /api/me/games/{id}/open: статус публикации + счётчик попыток. */
export interface OpenStatus {
  published: boolean;
  slug?: string;
  url?: string;
  publishedAt?: string;
  attemptCount: number;
  attemptLimit: number;
  /** true — лимит попыток исчерпан (приём новых закрыт, Ц5). */
  closed: boolean;
}

/** Строка листинга попыток (GET /api/me/games/{id}/open/attempts). */
export interface OpenAttemptItem {
  id: number;
  nickname: string;
  /** active | finished | abandoned. */
  status: string;
  startedAt: string;
  /** "" пока активна. */
  finishedAt: string;
  score: number;
  answerCount: number;
  correctCount: number;
}

/** Ответ GET /api/me/games/{id}/open/attempts?limit&offset. */
export interface OpenAttemptsResp {
  items: OpenAttemptItem[];
  total: number;
}

/** Ответ детальной попытки: что ответил игрок + verdict + reveal (§C4.3). */
export interface OpenAttemptDetail {
  attemptId: number;
  gameId: number;
  gameTitle: string;
  nickname: string;
  status: string;
  startedAt: string;
  finishedAt: string;
  score: number;
  totalQuestions: number;
  answers: Array<{
    questionId: number;
    kind: string;
    option: number;
    payload?: Record<string, unknown>;
    correct: boolean;
    points: number;
    reveal?: OpenRevealData;
  }>;
}

/** Агрегат одного вопроса (GET /api/me/games/{id}/open/stats). */
export interface OpenQuestionStat {
  questionId: number;
  total: number;
  correct: number;
  avgPoints: number;
}

/** Ответ GET /api/me/games/{id}/open/stats. */
export interface OpenQuizStats {
  questions: OpenQuestionStat[];
  attemptsTotal: number;
  attemptsFinished: number;
  avgScore: number;
}

/**
 * POST /api/me/games/{id}/open/publish → 201 OpenPublishResp. Повторная
 * публикация уже опубликованного = ротация slug (слот не расходуется).
 * 409 limit_open_quizzes + meta {limit,used}; 403 не-quiz; 404 чужое.
 */
export function publishOpenQuiz(
  gameId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenPublishResp> {
  return request<OpenPublishResp>(
    `/api/me/games/${gameId}/open/publish`,
    { method: 'POST' },
    fetchImpl,
  );
}

/** POST /api/me/games/{id}/open/unpublish → 200; история попыток остаётся. */
export function unpublishOpenQuiz(
  gameId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<{ unpublished: number }> {
  return request<{ unpublished: number }>(
    `/api/me/games/${gameId}/open/unpublish`,
    { method: 'POST' },
    fetchImpl,
  );
}

/** GET /api/me/games/{id}/open → 200 OpenStatus (счётчики для панели владельца). */
export function fetchOpenStatus(
  gameId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenStatus> {
  return request<OpenStatus>(`/api/me/games/${gameId}/open`, { method: 'GET' }, fetchImpl);
}

/** GET /api/me/games/{id}/open/attempts?limit&offset → {items,total}. */
export function fetchOpenAttempts(
  gameId: number,
  opts: { limit?: number; offset?: number; fetchImpl?: typeof fetch } = {},
): Promise<OpenAttemptsResp> {
  const { limit, offset, fetchImpl = fetch } = opts;
  const query = new URLSearchParams();
  if (limit !== undefined && limit > 0) query.set('limit', String(limit));
  if (offset !== undefined && offset > 0) query.set('offset', String(offset));
  const qs = query.toString();
  const path = `/api/me/games/${gameId}/open/attempts${qs ? `?${qs}` : ''}`;
  return request<OpenAttemptsResp>(path, { method: 'GET' }, fetchImpl);
}

/** GET /api/me/open/attempts/{attemptId} → детальная с ответами+reveal. */
export function fetchOpenAttemptDetail(
  attemptId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenAttemptDetail> {
  return request<OpenAttemptDetail>(
    `/api/me/open/attempts/${attemptId}`,
    { method: 'GET' },
    fetchImpl,
  );
}

/** GET /api/me/games/{id}/open/stats → агрегаты по вопросам + сводные. */
export function fetchOpenQuizStats(
  gameId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenQuizStats> {
  return request<OpenQuizStats>(`/api/me/games/${gameId}/open/stats`, { method: 'GET' }, fetchImpl);
}

/**
 * DELETE /api/me/games/{id}/open/attempts {"confirm":true} → {deleted:n}.
 * Безвозвратно удаляет всю историю попыток квиза (сброс счётчика, Ц5);
 * подтверждение — window.confirm на клиенте (О8/В4).
 */
export function resetOpenAttempts(
  gameId: number,
  fetchImpl: typeof fetch = fetch,
): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(
    `/api/me/games/${gameId}/open/attempts`,
    { method: 'DELETE', headers: JSON_HEADERS, body: JSON.stringify({ confirm: true }) },
    fetchImpl,
  );
}
